/**
 * Fold one pushed record into a cached list.
 *
 * Undefined in, undefined out: a message can land before the first fetch resolves, and
 * seeding the list from that one message would render a screen holding a single child as
 * though that were the whole room. The snapshot is REST's job; this only ever edits a list
 * that already exists.
 *
 * Returns a new array — react-query compares by identity, and mutating in place renders nothing.
 */
export function patchList<T>(list: T[] | undefined, item: T, idOf: (row: T) => string): T[] | undefined {
  if (!list) return list;
  const id = idOf(item);
  const index = list.findIndex(row => idOf(row) === id);
  if (index === -1) return [...list, item];
  const next = [...list];
  next[index] = item;
  return next;
}

/**
 * Fold one pushed record into a cached list, but only when that record's id is already a
 * member of the list — never append.
 *
 * For a list backed by an unfiltered snapshot (e.g. checkout calls, where a brand-new call
 * must appear on a screen that has never seen it), `patchList`'s append-on-miss is correct.
 * But some lists are themselves a *filtered view* — a grid scoped to one grade, one role, one
 * search term — where the fetch, not a later push, is what defines membership. A pushed row
 * for someone outside that filter is not "a member we haven't fetched yet"; it's someone this
 * screen was never supposed to show, and appending it would put a person in a grade-7 roster
 * who belongs in grade 4. Replace-if-present, ignore-if-absent is the correct rule there.
 *
 * Returns the SAME reference when the id is absent (nothing to do — no new array, no re-render
 * for a change that doesn't apply here) and a NEW array when it replaces a row, so react-query's
 * identity-based comparison still sees exactly the updates that matter.
 */
export function replaceInList<T>(list: T[] | undefined, item: T, idOf: (row: T) => string): T[] | undefined {
  if (!list) return list;
  const id = idOf(item);
  const index = list.findIndex(row => idOf(row) === id);
  if (index === -1) return list;
  const next = [...list];
  next[index] = item;
  return next;
}
